/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * TV Device Detection Utility
 *
 * Detects whether the current device is a TV (Android TV, Fire TV, Smart TVs).
 * Uses a combination of user-agent sniffing and CSS media query heuristics.
 *
 * NOTE: Android TV D-pads report `pointer: none` (NOT `pointer: coarse`)
 * because a D-pad is not a pointing device. We use `hover: none` + large
 * screen as the reliable combination.
 */

let _cachedResult = null; // Preserve existing caching pattern
let _cachedIsTWA = null;

// Returns true for ANY TV device (TWA or browser)
export function isTVDevice() {
  if (_cachedResult !== null) return _cachedResult;
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase(); // Case-insensitive

  // PRIMARY: Explicit TWA signal (set by our own Android wrapper)
  if (ua.includes('indikintwa/tv')) { _cachedResult = true; return true; }

  // SECONDARY: Known TV platform keywords (case-insensitive)
  const knownTVKeywords = [
    'googletv', 'aft', 'bravia', 'crkey',
    'smarttv', 'tizen', 'web0s', 'aftn',
  ];
  _cachedResult = knownTVKeywords.some(kw => ua.includes(kw));
  return _cachedResult;
  // NEVER return true based on media queries alone
}

// Returns true ONLY for our TWA wrapper (not generic TV browsers)
// Use this for destructive actions that must NOT apply to generic TV browser users: blocking purchases, exit intent suppression
export function isTWA() {
  if (_cachedIsTWA !== null) return _cachedIsTWA;
  if (typeof window === 'undefined') return false;
  _cachedIsTWA = navigator.userAgent.toLowerCase().includes('indikintwa/tv');
  return _cachedIsTWA;
}

// Apply body class for CSS scoping
if (typeof window !== 'undefined' && isTVDevice()) {
  document.body.classList.add('is-tv');
}
